/**
 * Error Handling Utility
 * Provides centralized error handling and user-friendly error messages
 */

/**
 * Error types
 */
export const ERROR_TYPES = {
  VALIDATION: 'VALIDATION_ERROR',
  AUTHENTICATION: 'AUTHENTICATION_ERROR',
  AUTHORIZATION: 'AUTHORIZATION_ERROR',
  NETWORK: 'NETWORK_ERROR',
  NOT_FOUND: 'NOT_FOUND_ERROR',
  SERVER: 'SERVER_ERROR',
  PAYMENT: 'PAYMENT_ERROR',
  UNKNOWN: 'UNKNOWN_ERROR'
};

/**
 * Error severity levels
 */
export const ERROR_SEVERITY = {
  LOW: 'low',
  MEDIUM: 'medium',
  HIGH: 'high',
  CRITICAL: 'critical'
};

/**
 * Get user-friendly error message
 * @param {Error} error - Error object
 * @returns {string} User-friendly message
 */
export const getUserFriendlyMessage = (error) => {
  // If error already has a user-friendly message
  if (error.userMessage) {
    return error.userMessage;
  }

  // Handle specific error types
  if (error.code === 'auth/user-not-found') {
    return 'User account not found. Please check your email address.';
  }
  if (error.code === 'auth/wrong-password') {
    return 'Incorrect password. Please try again.';
  }
  if (error.code === 'auth/email-already-in-use') {
    return 'This email is already registered. Please use a different email or try logging in.';
  }
  if (error.code === 'auth/weak-password') {
    return 'Password is too weak. Please use at least 8 characters with a mix of letters and numbers.';
  }
  if (error.code === 'auth/network-request-failed') {
    return 'Network error. Please check your internet connection and try again.';
  }
  if (error.code === 'permission-denied') {
    return 'You do not have permission to perform this action.';
  }
  if (error.code === 'unavailable') {
    return 'Service temporarily unavailable. Please try again later.';
  }
  if (error.code === 'deadline-exceeded') {
    return 'Request timed out. Please try again.';
  }

  // Handle validation errors
  if (error.name === 'ValidationError') {
    return error.message || 'Please check your input and try again.';
  }

  // Handle Firebase errors
  if (error.code && error.code.startsWith('auth/')) {
    return 'Authentication error. Please try again or contact support.';
  }
  if (error.code && error.code.startsWith('firestore/')) {
    return 'Database error. Please try again or contact support.';
  }

  // Generic error messages based on error type
  if (error.type === ERROR_TYPES.VALIDATION) {
    return 'Please check your input and try again.';
  }
  if (error.type === ERROR_TYPES.AUTHENTICATION) {
    return 'Please log in to continue.';
  }
  if (error.type === ERROR_TYPES.AUTHORIZATION) {
    return 'You do not have permission to perform this action.';
  }
  if (error.type === ERROR_TYPES.NETWORK) {
    return 'Network error. Please check your connection and try again.';
  }
  if (error.type === ERROR_TYPES.PAYMENT) {
    return 'Payment processing error. Please try again or contact support.';
  }

  // Default message
  return error.message || 'An unexpected error occurred. Please try again or contact support.';
};

/**
 * Log error for monitoring
 * @param {Error} error - Error object
 * @param {Object} context - Additional context
 */
export const logError = (error, context = {}) => {
  // In production, send to error tracking service (Sentry, etc.)
  const errorLog = {
    message: error.message,
    stack: error.stack,
    code: error.code,
    type: error.type || ERROR_TYPES.UNKNOWN,
    severity: error.severity || ERROR_SEVERITY.MEDIUM,
    context,
    timestamp: new Date().toISOString(),
    userAgent: typeof window !== 'undefined' ? window.navigator.userAgent : 'server',
    url: typeof window !== 'undefined' ? window.location.href : 'server'
  };

  // Log to console in development
  if (process.env.NODE_ENV === 'development') {
    console.error('Error logged:', errorLog);
  }

  // TODO: Send to error tracking service in production
  // Example: Sentry.captureException(error, { extra: context });
};

/**
 * Handle error and return user-friendly response
 * @param {Error} error - Error object
 * @param {Object} options - Options
 * @returns {Object} Error response
 */
export const handleError = (error, options = {}) => {
  const {
    showToUser = true,
    logError: shouldLog = true,
    defaultMessage = 'An error occurred. Please try again.',
    context = {}
  } = options;

  // Log error
  if (shouldLog) {
    logError(error, context);
  }

  // Get user-friendly message
  const userMessage = showToUser ? getUserFriendlyMessage(error) : defaultMessage;

  // Return error response
  return {
    success: false,
    error: error.message || 'Unknown error',
    userMessage,
    code: error.code,
    type: error.type || ERROR_TYPES.UNKNOWN,
    severity: error.severity || ERROR_SEVERITY.MEDIUM
  };
};

/**
 * Create error with type and severity
 * @param {string} message - Error message
 * @param {string} type - Error type
 * @param {string} severity - Error severity
 * @returns {Error} Error object
 */
export const createError = (message, type = ERROR_TYPES.UNKNOWN, severity = ERROR_SEVERITY.MEDIUM) => {
  const error = new Error(message);
  error.type = type;
  error.severity = severity;
  return error;
};

/**
 * Wrap async function with error handling
 * @param {Function} asyncFn - Async function to wrap
 * @param {Object} options - Options
 * @returns {Function} Wrapped function
 */
export const withErrorHandling = (asyncFn, options = {}) => {
  return async (...args) => {
    try {
      return await asyncFn(...args);
    } catch (error) {
      return handleError(error, options);
    }
  };
};

/**
 * Handle API errors
 * @param {Error} error - Error object
 * @param {Object} context - Context
 * @returns {Object} Error response
 */
export const handleAPIError = (error, context = {}) => {
  // Determine error type
  let errorType = ERROR_TYPES.UNKNOWN;
  let severity = ERROR_SEVERITY.MEDIUM;

  if (error.code === 'permission-denied') {
    errorType = ERROR_TYPES.AUTHORIZATION;
    severity = ERROR_SEVERITY.HIGH;
  } else if (error.code === 'unavailable' || error.code === 'deadline-exceeded') {
    errorType = ERROR_TYPES.NETWORK;
    severity = ERROR_SEVERITY.MEDIUM;
  } else if (error.code && error.code.startsWith('auth/')) {
    errorType = ERROR_TYPES.AUTHENTICATION;
    severity = ERROR_SEVERITY.HIGH;
  }

  return handleError(error, {
    context: { ...context, apiError: true },
    defaultMessage: 'API request failed. Please try again.'
  });
};

